import Cookies from 'js-cookie';
import React, {
  useState,
  useEffect,
  useRef,
  useCallback,
  useImperativeHandle,
} from 'react';
import MaskedInput from 'react-text-mask';

import {
  compact,
  fill,
  filter,
  indexOf,
  map,
  replace,
  startsWith,
  trimStart,
} from 'lodash';

import { fetcher } from '@/api/fetcher';
import { requests } from '@/api/requests';

import { openModal } from '@/services/modalsProvider';

import { DOCUMENT_MODAL_TYPES, MODAL_NAMES } from '@/utils/constants';
import { isDevelopment } from '@/utils/helpers';
import { trackUserAction } from '@/utils/trackUserAction';

import { useBreakpoints } from '@/hooks/use-breakpoints';

import css from './auth-form.module.scss';

const PHONE_SYMBOLS_REGEX = /^\+?[\d()-\s]+$/;
const PHONE_MASK_LENGTH = 18;

const isPhone = (phone) => {
  if (PHONE_SYMBOLS_REGEX.test(phone)) {
    const cleanedPhone = replace(phone, /\D/g, '');

    if (/^(\+7|7|8|9)/.test(phone)) {
      return cleanedPhone.length <= 11;
    }

    if (/^(\+3|3)/.test(phone)) {
      return cleanedPhone.length <= 12;
    }
  }

  return false;
};

const phoneStartsWith = (phone, phoneCode) => {
  if (!isPhone(phone)) {
    return false;
  }
  return startsWith(phone, phoneCode);
};

const getInputMask = (value) => {
  switch (true) {
    case phoneStartsWith(value, '+7'):
      return [
        /\+/,
        /\d/,
        ' ',
        '(',
        /\d/,
        /\d/,
        /\d/,
        ')',
        ' ',
        /\d/,
        /\d/,
        /\d/,
        '-',
        /\d/,
        /\d/,
        '-',
        /\d/,
        /\d/,
      ];

    case phoneStartsWith(value, '9'):
      return [
        '+',
        '7',
        ' ',
        '(',
        /\d/,
        /\d/,
        /\d/,
        ')',
        ' ',
        /\d/,
        /\d/,
        /\d/,
        '-',
        /\d/,
        /\d/,
        '-',
        /\d/,
        /\d/,
      ];

    case phoneStartsWith(value, '8'):
      return [
        /\d/,
        ' ',
        '(',
        /\d/,
        /\d/,
        /\d/,
        ')',
        ' ',
        /\d/,
        /\d/,
        /\d/,
        '-',
        /\d/,
        /\d/,
        '-',
        /\d/,
        /\d/,
      ];

    case phoneStartsWith(value, '7'):
      return [
        '+',
        /\d/,
        ' ',
        '(',
        /\d/,
        /\d/,
        /\d/,
        ')',
        ' ',
        /\d/,
        /\d/,
        /\d/,
        '-',
        /\d/,
        /\d/,
        '-',
        /\d/,
        /\d/,
      ];

    case phoneStartsWith(value, '+3'):
      return [
        '+',
        '3',
        /\d/,
        /\d/,
        ' ',
        '(',
        /\d/,
        /\d/,
        ')',
        ' ',
        /\d/,
        /\d/,
        /\d/,
        '-',
        /\d/,
        /\d/,
        '-',
        /\d/,
        /\d/,
      ];

    case phoneStartsWith(value, '3'):
      return [
        '+',
        /\d/,
        /\d/,
        /\d/,
        ' ',
        '(',
        /\d/,
        /\d/,
        // /\d/,
        ')',
        ' ',
        /\d/,
        /\d/,
        /\d/,
        '-',
        /\d/,
        /\d/,
        '-',
        /\d/,
        /\d/,
      ];

    default:
      return fill(Array(256), /[\w+.@А-я-]/);
  }
};

const extractRawValue = (rawValue, mask, caretPosition) => {
  const maskedSymbols = filter(mask, (s) => {
    return !(s instanceof RegExp);
  });
  const cleanedValue = map(rawValue, (s, i) => {
    const maskedSymbolIndex = indexOf(maskedSymbols, s);

    if (maskedSymbolIndex > -1 && i + 1 !== caretPosition) {
      maskedSymbols.splice(maskedSymbolIndex, 1);

      return '';
    }

    return s;
  });

  return compact(cleanedValue).join('');
};

const LoginInput = React.forwardRef(({ setInputValue, onKeyPress }, ref) => {
  const maskedInputRef = useRef(null);

  const lastPhoneCode = useRef('');
  const currentMask = useRef([]);
  const lastMask = useRef([]);

  function phoneMask(rawValue) {
    const mask = getInputMask(rawValue);
    const phoneCode = rawValue.substr(0, 2);

    if (
      currentMask.current.length === mask.length &&
      lastPhoneCode.current === phoneCode
    ) {
      return currentMask.current;
    }

    currentMask.current = mask;

    return mask;
  }

  const focus = useCallback(() => {
    const maskedInputInstance = maskedInputRef.current;

    if (!maskedInputInstance) {
      return;
    }

    const inputEl = maskedInputInstance.inputElement;

    if (!inputEl) {
      return;
    }

    const isAlreadyFocused = document.activeElement === inputEl;

    if (isAlreadyFocused) {
      return;
    }

    inputEl.focus();
  }, []);

  useImperativeHandle(ref, () => {
    return {
      focus,
    };
  }, [focus]);

  return (
    <MaskedInput
      className={css.input}
      onChange={(e) => {
        setInputValue(e.target.value);
      }}
      ref={maskedInputRef}
      onKeyPress={onKeyPress}
      placeholder='Введите email'
      mask={phoneMask}
      guide={false}
      pipe={(conformedValue, config) => {
        let finalValue = conformedValue;

        if (
          config.rawValue &&
          lastMask.current.length <= PHONE_MASK_LENGTH &&
          currentMask.current.length > PHONE_MASK_LENGTH
        ) {
          finalValue = extractRawValue(
            config.rawValue,
            lastMask.current,
            config.currentCaretPosition
          );
        }

        lastPhoneCode.current = finalValue.substr(0, 2);
        lastMask.current = currentMask.current;

        return finalValue;
      }}
    />
  );
});

const REGISTERED_STATES = {
  unknown: 'unknown',
  registered: 'registered',
  unregistered: 'unregistered',
};

const EMAIL_REGEX =
  /^([\w-]+(?:\.[\w-]+)*)@((?:[\w-]+\.)*\w[\w-]{0,66})\.([a-z]{2,11}(?:\.[a-z]{2,11})?)$/i;

const AuthForm = ({ loginInputRef }) => {
  const bps = useBreakpoints();
  const hasWindow = typeof window !== 'undefined';
  const root = hasWindow && window.document.documentElement;

  const inputBlockRef = useRef(null);
  const [inputValue, setInputValue] = useState('');
  const [isRegistered, setIsRegistered] = useState(REGISTERED_STATES.unknown);

  // TODO: Отменять неактуальные запросы
  const checkUser = async (data) => {
    let isRegisteredResponse;
    try {
      const loginData = await fetcher(requests.checkLogin, data.email);

      const parsedLoginData =
        typeof loginData === 'string'
          ? (() => {
              try {
                return JSON.parse(loginData);
              } catch (_) {
                return null;
              }
            })()
          : loginData;

      if (parsedLoginData?.status === 'used') {
        isRegisteredResponse = true;
      } else if (parsedLoginData?.status === 'free') {
        isRegisteredResponse = false;
      } else {
        isRegisteredResponse = parsedLoginData?.registered || false;
      }
    } catch (_) {
      isRegisteredResponse = false;
    }

    setIsRegistered(
      isRegisteredResponse
        ? REGISTERED_STATES.registered
        : REGISTERED_STATES.unregistered
    );
  };

  useEffect(() => {
    let loginToCheck;

    if (EMAIL_REGEX.test(inputValue)) {
      loginToCheck = inputValue;
    }

    const phoneNumber = replace(inputValue, /[ ()_-]/g, '');
    if (phoneStartsWith(phoneNumber, '+3') && phoneNumber.length === 13) {
      loginToCheck = phoneNumber;
    }
    if (phoneStartsWith(phoneNumber, '+7') && phoneNumber.length === 12) {
      loginToCheck = phoneNumber;
    }
    if (phoneStartsWith(phoneNumber, '8') && phoneNumber.length === 11) {
      loginToCheck = `+7${trimStart(phoneNumber, '8')}`;
    }

    setIsRegistered(REGISTERED_STATES.unknown);

    if (!loginToCheck) {
      return undefined;
    }

    const timeoutId = setTimeout(() => {
      checkUser({ email: loginToCheck });
    }, 1000);

    return () => {
      clearTimeout(timeoutId);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [inputValue]);

  const getTextWidth = (text, font) => {
    // re-use canvas object for better performance
    const canvas =
      getTextWidth.canvas ||
      (getTextWidth.canvas = document.createElement('canvas'));
    const context = canvas.getContext('2d');
    context.font = font;
    const metrics = context.measureText(text);
    return metrics.width;
  };

  useEffect(() => {
    const textWidth = getTextWidth(inputValue, 'normal 20px Montserrat');

    if (!root) {
      return;
    }
    if (textWidth > 375) {
      if (bps.desktop) {
        root.style.setProperty('--js-width-auth-modal', `${textWidth + 425}px`);
      }
    } else {
      root.style.setProperty(
        '--js-width-auth-modal',
        bps.desktop ? `800px` : `700px`
      );
    }
    if (textWidth > 310) {
      if (!bps.desktop) {
        root.style.setProperty('--js-width-auth-modal', `${textWidth + 390}px`);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [inputValue]);

  const getHref = () => {
    const baseUrl = isDevelopment
      ? 'http://localhost:3000'
      : hasWindow
        ? 'https://auth.amocrm.ru'
        : '';

    if (isRegistered === REGISTERED_STATES.unregistered) {
      return `${baseUrl}/activate?login=${encodeURIComponent(inputValue)}`;
    }

    if (isRegistered === REGISTERED_STATES.registered) {
      return `${baseUrl}/auth/amochats?login=${encodeURIComponent(inputValue)}`;
    }

    return undefined;
  };

  const onClick = () => {
    Cookies.remove('utm-links');
    if (isRegistered === REGISTERED_STATES.unregistered) {
      trackUserAction('click_web');
    }
  };

  const onKeyPress = (e) => {
    // TODO: Use keyCode
    if (e.key === 'Enter' && isRegistered !== REGISTERED_STATES.unknown) {
      if (isRegistered === REGISTERED_STATES.unregistered) {
        trackUserAction('click_web');
      }
      Cookies.remove('utm-links');
      window.location.href = getHref();
    }
  };

  return (
    <div className={css.inputBlock} ref={inputBlockRef}>
      <LoginInput
        onKeyPress={onKeyPress}
        setInputValue={setInputValue}
        checkUser={checkUser}
        ref={loginInputRef}
      />
      {isRegistered !== REGISTERED_STATES.unknown && (
        <a href={getHref()} className={css.inputButton} onClick={onClick}>
          {isRegistered === REGISTERED_STATES.unregistered
            ? 'Зарегистрироваться'
            : 'Войти'}
        </a>
      )}
      {/** FIXME: Этот компонент должен находиться в родителе */}
      {isRegistered === REGISTERED_STATES.unregistered && (
        <div className={css.confidence}>
          Нажимая на кнопку, вы даете согласие на обработку{' '}
          <button
            className={css.confidenceLink}
            type='button'
            onClick={() => {
              openModal({
                id: DOCUMENT_MODAL_TYPES.confidence,
                modalName: MODAL_NAMES.document,
                props: {
                  docType: DOCUMENT_MODAL_TYPES.confidence,
                  invert: true,
                  withBackground: false,
                },
              });
            }}
          >
            персональных данных
          </button>
        </div>
      )}
    </div>
  );
};

export default AuthForm;
